import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { ISession } from '../../../../models/session';
import { FIELD_TYPES, ICompanionsData, IFieldValue, IRegistrationField, IRegistrationFormOptions, MULTIVALUE_TYPES, RegistrationFormService } from '../../registration-form.service';
import { PendingOp } from '../edit-registration-field-values/edit-registration-field-values.component';
import { ConditionPendingOp, NEW_FIELD_PLACEHOLDER_ID } from '../edit-registration-field-conditions/edit-registration-field-conditions.component';
import 'rxjs/add/operator/concatMap';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/toArray';
import 'rxjs/add/observable/from';
import 'rxjs/add/observable/of';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'edit-registration-field',
  templateUrl: './edit-registration-field.component.html',
})
export class EditRegistrationFieldComponent implements OnInit, OnChanges {
  @Input() session: ISession;
  @Input() field: IRegistrationField | null = null;
  @Input() allFields: IRegistrationField[] = [];
  @Input() options: IRegistrationFormOptions = {
    inUseOnly: false,
    displayKind: false,
    allowQuotas: false,
    allowCompanions: false,
    allowConditions: false,
    allowWaitingList: false,
  };
  @Output() saved = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  public multivalueTypes = MULTIVALUE_TYPES;

  get fieldTypes(): string[] {
    return this.options.allowCompanions
      ? FIELD_TYPES
      : FIELD_TYPES.filter(t => t !== 'accompagnants');
  }
  public isSaving = false;
  public pendingValueOps: PendingOp[] = [];
  public pendingConditionOps: ConditionPendingOp[] = [];
  public activeTab: 'general' | 'options' | 'companions' = 'general';

  public label = '';
  public fieldType = 'text';
  public isInUse = false;
  public required = false;
  public requiredDependent = false;
  public fieldHasDependancies = false;
  public dependsOnOtherField = false;
  public displayRemainingQuota: 'no' | 'values' | 'indicator' = 'no';
  public companionsData: ICompanionsData | null = null;
  public pendingCompanionsData: ICompanionsData | null = null;

  constructor(private registrationFormService: RegistrationFormService, private translate: TranslateService) {}

  ngOnInit() {
    this.initFromField();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.companionsData) {
      this.initFromField();
      this.pendingValueOps = [];
      this.pendingConditionOps = [];
      this.activeTab = 'general';
    }
    if (changes.allFields) {
      this.fieldHasDependancies = this.field ? this.hasDependancies(this.field.option_id) : false;
    }
    if (changes.field) {
      if (this.field && this.field.required) {
        this.dependsOnOtherField = false;
      }
    }
  }

  get isDefault(): boolean {
    return !!(this.field && this.field.is_default);
  }

  get nameDisplay(): string {
    return this.field ? (this.field.name || '') : '';
  }

  get isMultivalue(): boolean {
    return this.multivalueTypes.indexOf(this.fieldType) >= 0;
  }

  get isAccompagnants(): boolean {
    return !this.isDefault && this.fieldType === 'accompagnants';
  }

  canSave(): boolean {
    if (!this.label) return false;
    if (this.isAccompagnants && this.pendingCompanionsData) {
      const d = this.pendingCompanionsData;
      if (d.companions_options === 'conditional' && !d.linked_option_id) return false;
      if (d.companions_options === 'individual' && !d.number_option_id) return false;
    }
    return true;
  }

  onFieldTypeChange(newType: string) {
    this.fieldType = newType;
    this.activeTab = this.isMultivalue ? 'options' : 'general';
  }

  onPendingOpsChange(ops: PendingOp[]) {
    this.pendingValueOps = ops;
  }

  onConditionOpsChange(ops: ConditionPendingOp[]) {
    this.pendingConditionOps = ops;
  }

  onCompanionsDataChanged(data: ICompanionsData | null) {
    this.pendingCompanionsData = data;
  }

  private initFromField() {
    if (this.field) {
      this.label = this.field.label || '';
      this.fieldType = this.field.field_type || 'text';
      this.isInUse = !!this.field.is_in_use;
      this.required = !!this.field.required;
      this.requiredDependent = !!this.field.required_dependent;
      this.displayRemainingQuota = this.field.display_remaining_quota || 'no';
      this.companionsData = this.field.companions_data || null;
      this.pendingCompanionsData = { companions_options: 'global', ...this.companionsData };
      this.dependsOnOtherField = this.fieldHasDependancies;
      // this.hasDependancies(this.field.option_id);
    } else {
      this.label = '';
      this.fieldType = 'text';
      this.isInUse = false;
      this.required = false;
      this.requiredDependent = false;
      this.displayRemainingQuota = 'no';
      this.companionsData = null;
      this.pendingCompanionsData = { companions_options: 'global' };
      this.dependsOnOtherField = false;
    }
  }

  save() {
    if (this.isSaving) return;
    this.isSaving = true;

    const baseDto: any = {
      label: this.label,
      required: this.required,
    };

    let fieldSave$: Observable<any>;

    if (this.field) {
      fieldSave$ = this.field.is_default
        ? this.registrationFormService.updateDefaultField(
            this.session, this.field.option_id,
            { ...baseDto, is_in_use: this.isInUse },
            false
          )
        : this.registrationFormService.updateCustomField(
            this.session, this.field.option_id,
            { 
              ...baseDto, 
              type: this.fieldType, 
              required_dependent: this.requiredDependent, 
              display_remaining_quota: 'values' // hide this option and force to use value: values EVA-188
            },
            false
          );
    } else {
      fieldSave$ = this.registrationFormService.createCustomField(this.session, {
        ...baseDto,
        type: this.fieldType,
        required_dependent: this.requiredDependent,
        display_remaining_quota: 'values', // hide this option and force to use value: values EVA-188
        ...(this.pendingCompanionsData && this.fieldType === 'accompagnants' && { companions_data: this.pendingCompanionsData }),
      }, false);
    }

    fieldSave$
      .concatMap((f: IRegistrationField) => this.executePendingOps(f).map(() => f))
      .concatMap((f: IRegistrationField) => this.executeCompanionsSave(f).map(() => f))
      .concatMap((f: IRegistrationField) => this.executeConditionOps(f))
      .subscribe(
        () => { 
          this.isSaving = false; 
          this.saved.emit(); 
          toastr.success(this.translate.instant('action.success'));
        },
        () => { this.isSaving = false; }
      );
  }

  private executeConditionOps(field: IRegistrationField): Observable<any> {
    if (!this.pendingConditionOps.length) {
      return Observable.of(field);
    }
    const isNewField = !this.field;
    return Observable.from(this.pendingConditionOps).concatMap((op: ConditionPendingOp) => {
      const questionIds = isNewField
        ? op.questionIds.map(id => id === NEW_FIELD_PLACEHOLDER_ID ? field.option_id : id)
        : op.questionIds;
      return this.registrationFormService.setValueConditions(this.session, op.valueId, questionIds, false);
    }).toArray();
  }

  private executeCompanionsSave(field: IRegistrationField): Observable<any> {
    if (!this.isAccompagnants || this.pendingCompanionsData === null) {
      return Observable.of(field);
    }
    return this.registrationFormService.setCompanions(this.session, field.option_id, this.pendingCompanionsData, false);
  }

  private executePendingOps(field: IRegistrationField): Observable<any> {
    if (!this.pendingValueOps.length || !field.option_id) {
      return Observable.of(null);
    }

    const optionId = field.option_id;
    const ops = this.pendingValueOps;
    const reorderOp = ops.find(op => op.kind === 'reorder') as Extract<PendingOp, { kind: 'reorder' }> | undefined;
    const tempIdMap = new Map<number, number>();

    return Observable.from(ops).concatMap((op: PendingOp) => {
      if (op.kind === 'reorder') {
        return Observable.of(null);
      }

      if (op.kind === 'delete') {
        return this.registrationFormService.deleteCustomFieldValue(this.session, optionId, op.valueId, false);
      }

      if (op.kind === 'create') {
        return this.registrationFormService.createCustomFieldValue(
          this.session, optionId, op.dto, false
        ).concatMap((created: IFieldValue) => {
          if (op.questionIds.length > 0) {
            return this.registrationFormService.setValueConditions(this.session, created.option_value_id, op.questionIds, false).map(() => created);
          }
          return Observable.of(created);
        }).do((created: IFieldValue) => tempIdMap.set(op.tempId, created.option_value_id));
      }

      // update
      const update$ = this.isDefault
        ? this.registrationFormService.updateDefaultFieldValue(this.session, optionId, op.valueId, op.dto, false)
        : this.registrationFormService.updateCustomFieldValue(this.session, optionId, op.valueId, op.dto, false);

      return update$.concatMap((updated: IFieldValue) => {
        const questionsChanged = this.didQuestionsChange(op.origQuestionIds, op.questionIds);
        if (questionsChanged) {
          return this.registrationFormService.setValueConditions(this.session, updated.option_value_id, op.questionIds, false).map(() => updated);
        }
        return Observable.of(updated);
      });
    }).toArray().concatMap(results => {
      if (!reorderOp || this.isDefault) {
        return Observable.of(results);
      }
      const order = reorderOp.order.map(id => tempIdMap.has(id) ? tempIdMap.get(id) : id);
      return this.registrationFormService.reorderCustomFieldValues(this.session, optionId, order, false).map(() => results);
    });
  }

  private didQuestionsChange(orig: number[], curr: number[]): boolean {
    if (orig.length !== curr.length) return true;
    const sortedOrig = [...orig].sort((a, b) => a - b);
    const sortedCurr = [...curr].sort((a, b) => a - b);
    return sortedOrig.some((id, i) => id !== sortedCurr[i]);
  }

  cancel() {
    this.cancelled.emit();
  }

  hasDependancies(optionId: number): boolean {
    return this.allFields.some(field =>
      (field.values || []).some(value =>
        (value.questions_to_display || []).indexOf(optionId) >= 0,
      ),
    );
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/sessions/registration-form/edit-registration-field/edit-registration-field.component.ts